/* eslint-disable react-refresh/only-export-components -- entry-tree builders:
   the Inner component + the two tree factories are one cohesive seam shared by
   both entries and the hydration test; no refreshable state lives here. */
import { StrictMode } from 'react';
import { BrowserRouter, StaticRouter } from 'react-router-dom';

import { MarketingAnalyticsProvider } from './analytics';
import { App } from './App';
import { I18nProvider } from './core/I18nProvider';
import { SeoCollectorProvider, type SeoCollector } from './lib/seo/seo-collector';

import type { Translator } from './i18n/translator';
import type { Locale } from '@syncanix/shared';
import type { ReactElement } from 'react';

/**
 * The ONE component tree both entries render (launch-readiness B2).
 *
 * `useId` derives ids from a component's position in the React tree, so the
 * server and client trees must be structurally IDENTICAL or every `useId`-based
 * `aria-labelledby`/`htmlFor` mismatches at hydration (the longstanding
 * React #418/#423 console errors on chrome-bearing routes — server tree had
 * SeoCollectorProvider but no analytics provider or StrictMode; client had the
 * inverse). Both entries now render the SAME layers in the SAME order; only
 * the router implementation differs (Static vs Browser — same internal
 * context-provider shape).
 *
 * The hydration smoke test (`hydration.test.tsx`) builds its trees from these
 * exact helpers, so the entries and the test cannot drift apart.
 */

export interface AppTreeOptions {
  /** Resolved document locale (URL-derived for docs; default elsewhere). */
  readonly locale?: Locale | undefined;
  /** Pre-resolved translator (SSG always; client when the locale is non-EN). */
  readonly translator?: Translator | undefined;
  /** SEO sink — the prerender collector on the server; `null` on the client
   *  (the provider stays mounted for tree parity; a null value makes
   *  `useSeoMeta` fall through to its direct DOM writes). */
  readonly seoSink: SeoCollector | null;
}

function Inner({ locale, translator }: Omit<AppTreeOptions, 'seoSink'>): ReactElement {
  return (
    <I18nProvider
      {...(locale !== undefined ? { initialLocale: locale } : {})}
      {...(translator !== undefined ? { initialTranslator: translator } : {})}
    >
      <MarketingAnalyticsProvider>
        <App />
      </MarketingAnalyticsProvider>
    </I18nProvider>
  );
}

/** The server (SSG) tree — StaticRouter pinned to `path`. */
export function buildServerTree(path: string, options: AppTreeOptions): ReactElement {
  return (
    <StrictMode>
      <SeoCollectorProvider value={options.seoSink}>
        <StaticRouter location={path}>
          <Inner locale={options.locale} translator={options.translator} />
        </StaticRouter>
      </SeoCollectorProvider>
    </StrictMode>
  );
}

/** The client tree — BrowserRouter over the live document URL. */
export function buildClientTree(options: AppTreeOptions): ReactElement {
  return (
    <StrictMode>
      <SeoCollectorProvider value={options.seoSink}>
        <BrowserRouter>
          <Inner locale={options.locale} translator={options.translator} />
        </BrowserRouter>
      </SeoCollectorProvider>
    </StrictMode>
  );
}
